import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { getConfig } from "../utils/config";
import { getStoryUrl } from "../utils/storyUrl";
import { getStoryReadyTime } from "../utils/storyReadyTime";
import { trackEvent } from "../utils/analytics";
import { decodeGuidToString, formatStoryCountdown, capitalize } from "../helpers";
import "./SessionCompleted.scss";
import getStoryData from "../utils/storyData";
import { StoryData, PlayerItem } from "../types/story";

const ordinalSuffix = (n: number) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

interface PlayerRowProps {
  player: PlayerItem;
  index: number;
  score: number;
  smallRankNums?: boolean;
  isLast?: boolean;
}

const PlayerRow: React.FC<PlayerRowProps> = ({ player, index, score, smallRankNums, isLast }) => (
  <>
    <div className={`lbRow ${index < 3 ? `lbRowRank${index + 1}` : ""} ${isLast ? "lbRowFaded" : ""}`}>
      <span className={`lbRowRankNum${smallRankNums ? " lbRowRankNumSm" : ""}`}>
        {index + 1}
        <span className="lbRowRankNumSuffix">{ordinalSuffix(index + 1)}</span>
      </span>

      <div className="lbRowAvatarWrap">
        <img
          className="lbRowAvatar"
          src={player.photo ? `data:image/jpeg;base64, ${player.photo}` : "./img/boy.png"}
          alt={player.name}
        />
      </div>
      <span className="name">{capitalize(player.name)}</span>
      <span className="points">
        {score} <span>Points</span>
      </span>
    </div>
  </>
);

const RE_VARIANT_VALUE = "A_leaderboard";

interface SessionCompletedAProps {
  isTestVariant?: boolean;
}

export const SessionCompletedA: React.FC<SessionCompletedAProps> = ({ isTestVariant = false }) => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("s");
  const sessionEndTime = searchParams.get("d");
  const storyReadyTime = sessionEndTime ? getStoryReadyTime(new Date(sessionEndTime)) : null;
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [storyData, setStoryData] = useState<StoryData | null>(null);

  const canViewStory = storyReadyTime ? currentTime >= storyReadyTime : false;
  const decodedSessionId = sessionId ? decodeGuidToString(sessionId) : null;

  const [storyUrl, setStoryUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    getStoryData(decodeGuidToString(sessionId)).then(setStoryData);
  }, [sessionId]);

  useEffect(() => {
    if (!isTestVariant) return;
    trackEvent({
      gaEvent: "rapid_entry_test_impression",
      params: { re_variant: RE_VARIANT_VALUE },
    });
  }, [isTestVariant]);

  const ctaVariantSuffix = isTestVariant ? `&re_variant=${RE_VARIANT_VALUE}` : "";

  const getScore = (player: PlayerItem) => player?.all_points ?? 0;

  const sortedPlayers = useMemo(
    () => [...(storyData?.Players ?? [])].sort((a, b) => getScore(b) - getScore(a)),
    [storyData],
  );

  useEffect(() => {
    if (!storyReadyTime || canViewStory) {
      return;
    }

    const intervalId = window.setInterval(() => setCurrentTime(new Date()), 1000);
    return () => window.clearInterval(intervalId);
  }, [storyReadyTime, canViewStory]);

  useEffect(() => {
    if (canViewStory) {
      getConfig()
        .then((config) => {
          setStoryUrl(getStoryUrl(config.storyRegionDomain, decodedSessionId));
        })
        .catch(() => {}); // fall back to "story not ready" copy
    }
  }, [canViewStory, decodedSessionId]);

  const storyCountdown = storyReadyTime
    ? formatStoryCountdown(Math.max(0, storyReadyTime.getTime() - currentTime.getTime()))
    : null;

  return (
    <div className="pageContainer">
      <div className="logoContainer">
        <img src={"./img/es-logo.svg"} width="110px" alt="Electric Shuffle" />
      </div>
      <div className="contentCard completeSession">
        <img src="./img/disco-ball.svg" className="discoBall" alt="Disco ball" />
        <div className="sessionCompletedContent">
          <div className="sessionCompletedTitle">Well Played!</div>
          {!storyUrl && (
            <>
              <div className="sessionCompletedText">
                We're putting together the best bits from your game in your own highlights reel. Check back
                when the countdown ends to see it!
                {storyCountdown && (
                  <span className="storyCountdown" aria-live="polite">
                    {storyCountdown}
                  </span>
                )}
              </div>
              <img src="./img/pucks-stars.svg" className="pucksStars" alt="Pucks and stars" />
            </>
          )}
          {storyUrl && (
            <>
              <div className="sessionCompletedText">
                Meet the leaderboard - then watch the game highlights in your Story.
              </div>
              <div className="leaderboard">
                {sortedPlayers.slice(0, 3).map((player, index) => (
                  <PlayerRow key={index} player={player} score={getScore(player)} index={index} />
                ))}
              </div>
              <div className="storyButtons">
                <button
                  className="small"
                  onClick={() => {
                    trackEvent({
                      gaEvent: "session_complete_story",
                      clarityEvent: "session_complete_story",
                    });
                  }}
                >
                  <a
                    className="viewStoryLink"
                    href={`${storyUrl}?utm_source=rapid_entry&utm_medium=session_complete_storytest${ctaVariantSuffix}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Enjoy your story
                  </a>
                </button>
                <button
                  className="small cream"
                  onClick={() => {
                    trackEvent({
                      gaEvent: "session_complete_book",
                      clarityEvent: "session_complete_book",
                    });
                  }}
                >
                  <a
                    className="orange"
                    href={`https://electricshuffle.com/?utm_source=rapid_entry&utm_medium=session_complete${ctaVariantSuffix}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book Next Round
                  </a>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {!storyUrl && (
        <footer className="btn-container">
          <button className="button">
            <a href="https://www.electricshuffle.com/" target="_blank" rel="noopener noreferrer">
              Book Next Round
            </a>
          </button>
        </footer>
      )}
    </div>
  );
};
